import { useState } from 'react';
import { useT } from '@/i18n/useT';
import { useBotHistory, useClearHistory } from '@/bot/hooks';
import type { TradeRecord } from '@/lib/api';
import { byMartingaleLevel } from '@/lib/mgLevels';
import { BotGate } from './BotGate';
import { BotNav } from './BotNav';
import { ConfirmButton } from './ConfirmButton';

const FILTERS = [
  { id: 'today', key: 'history.today' },
  { id: 'week', key: 'history.week' },
  { id: 'all', key: 'history.all' },
] as const;

const money = (n: number): string => `${n < 0 ? '-' : n > 0 ? '+' : ''}$${Math.abs(n).toFixed(2)}`;
/** "EURUSD_otc" → "EUR/USD OTC", as the approved rows read. */
const assetLabel = (code: string): string => {
  const otc = /_otc$/i.test(code);
  const base = code.replace(/_otc$/i, '');
  const pair = /^[A-Z]{6}$/i.test(base) ? `${base.slice(0, 3)}/${base.slice(3)}`.toUpperCase() : base;
  return otc ? `${pair} OTC` : pair;
};
const when = (ts: number): string =>
  ts ? new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

/** This customer's own trades. Reads stay available even when trading is
 *  blocked, so someone whose subscription lapsed can still see what happened. */
export function HistoryPage() {
  const { t } = useT();
  const [filter, setFilter] = useState<string>('today');
  const history = useBotHistory(filter);
  const clear = useClearHistory();

  const net = (items: TradeRecord[]): number => items.reduce((a, r) => a + (r.profitUsd ?? 0), 0);

  // Computed from the rows already on screen, so the summary always matches
  // the list beneath it and the active date filter rather than quietly
  // summarising something else. The maths lives in lib/mgLevels so it can be
  // tested; see r141.

  return (
    <BotGate query={history}>
      {(h) => (
        <>
          <div className="bot-seg wide">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className={filter === f.id ? 'on' : ''} onClick={() => setFilter(f.id)}>
                {t(f.key)}
              </button>
            ))}
          </div>

          <section className="bot-card">
            <div className="bot-card-head">
              <h2>{t('history.title')}</h2>
              <span className={net(h.items) >= 0 ? 'up' : 'down'}>
                {t('history.net')} {money(net(h.items))}
              </span>
            </div>

            {h.items.length === 0 ? null : (
              <div className="mg-levels">
                <h3 className="muted">{t('history.byLevel')}</h3>
                {byMartingaleLevel(h.items).map((lv) => (
                  <div key={lv.step} className="mg-level-row">
                    <span className="mg-level-name">
                      {lv.step === 0 ? t('history.noMg') : `MG${lv.step}`}
                    </span>
                    <span className="mg-level-stat">
                      {lv.wins} / {lv.trades} {t('history.wins')}
                    </span>
                    <span className="mg-level-stat">
                      {lv.winRate === null ? '-' : `${(lv.winRate * 100).toFixed(0)}%`}{' '}
                      {t('history.winRate')}
                    </span>
                    <span className={lv.net >= 0 ? 'up' : 'down'}>{money(lv.net)}</span>
                  </div>
                ))}
              </div>
            )}

            {h.items.length === 0 ? (
              <p className="muted">{t('history.empty')}</p>
            ) : (
              <ul className="bot-list">
                {h.items.map((r) => {
                  const o = String(r.outcome).toLowerCase();
                  // The approved history row: SKIPPED shows its reason and a
                  // dash, TIE shows a neutral $0.00, only WIN/LOSS are coloured.
                  const settled = o === 'win' || o === 'loss';
                  return (
                    <li key={r.id} className={`bot-list-row ${settled ? o : ''}`}>
                      <div className="bot-list-main">
                        <div>
                          <strong>{assetLabel(r.asset)}</strong>
                          <span className={r.direction === 'BUY' ? 'up' : 'down'}> {r.direction}</span>
                          {r.martingaleStep > 0 ? <span className="muted"> · MG{r.martingaleStep}</span> : null}
                        </div>
                        <div className="muted bot-list-sub">
                          {when(r.timestamp)}
                          {o === 'skipped'
                            ? (r.skipReason ? ` · ${r.skipReason}` : '')
                            : ` · ${t('history.stake', { amount: r.actualStake.toFixed(2) })}${r.payoutPct != null ? ` · ${r.payoutPct}%` : ''}`}
                        </div>
                        {/* The PRO automatic mode writes its selection decision
                            here: which pair won, why, and which were skipped.
                            Only rows that carry one render it, so ordinary
                            trades and every existing history row look exactly
                            as they did. */}
                        {r.detail ? <div className="muted bot-list-sub">{r.detail}</div> : null}
                      </div>
                      <div className="bot-list-right">
                        <span className={`bot-pill ${o}`}>{t(`status.${o}`)}</span>
                        <span className={settled ? (r.profitUsd >= 0 ? 'up strong' : 'down strong') : 'muted strong'}>
                          {o === 'skipped' ? '—' : money(r.profitUsd ?? 0)}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {h.items.length > 0 ? (
            <ConfirmButton
              label={t('history.clear')}
              confirmText={t('history.confirmClear')}
              busy={clear.isPending}
              onConfirm={() => clear.mutate()}
            />
          ) : null}

          <BotNav />
        </>
      )}
    </BotGate>
  );
}
